import moment from "moment";
import { getRowCustomization } from "./MarketSaleRowPro/customization";
import { PROPERTY_TYPE_OPTIONS, QUARTERLY_AREA_PROPERTY_KEYS } from "./constants";

export { PROPERTY_TYPE_OPTIONS } from "./constants";

/**
 * Build a single deduplicated list from all property lists on quarterlyAreaTable.
 * seen = Set of listingIDs already added (used to skip duplicates across keys).
 * result = combined list of properties, one per listingID (first occurrence kept).
 * Used as the source for "available to add" in AddPropertyFromListDialog.
 */
export function getCombinedPropertiesFromQuarterly(quarterlyAreaTable) {
  if (!quarterlyAreaTable) return [];
  const seen = new Set();
  const result = [];
  for (const key of QUARTERLY_AREA_PROPERTY_KEYS) {
    const list = quarterlyAreaTable[key];
    if (!Array.isArray(list)) continue;
    for (const p of list) {
      if (!p) continue;
      const id = getPropertyId(p);
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      result.push(p);
    }
  }
  return result;
}

/**
 * Unique id for a property row (listingID). MiniTablePro index sets listingID to a random id when missing.
 */
export function getPropertyId(p) {
  if (!p) return null;
  const lid = p.listingID;
  if (lid != null && String(lid).trim() !== "") return String(lid).trim();
  return null;
}

/**
 * Format date as "MMM D, YYYY"
 */
export function formatDate(date) {
  return moment(date).format("MMM D, YYYY");
}

/**
 * Today's date formatted
 */
export function getTodayDate() {
  return moment(new Date()).format("MMM D, YYYY");
}

/**
 * Oldest sale date from first 18 properties by saleDate
 */
export function getOldestDateFromPropList(propList) {
  if (!propList || propList.length === 0) return null;
  const sliced = propList
    .slice(0, 18)
    .filter((p) => p.saleDate && p.saleDate !== "-")
    .sort((a, b) => new Date(a.saleDate) - new Date(b.saleDate))[0];
  return sliced ? formatDate(sliced.saleDate) : null;
}

/**
 * Oldest sale date from sorted list (all items with saleDate !== "-")
 */
export function getOldestSaleDateFromSorted(sorted) {
//ignores null / undefined
//ignores empty strings
//ignores "-"
//ignores invalid date strings (new Date(value) invalid)
  if (!sorted || sorted.length === 0) return null;
  const dates = sorted
    .map((x) => x?.saleDate)
    .filter((d) => {
      if (d == null) return false;
      const value = String(d).trim();
      if (!value || value === "-") return false;
      return !Number.isNaN(new Date(value).getTime());
    })
    .sort((a, b) => new Date(a) - new Date(b));

  return dates[0] || null;
}

/**
 * Normalize brokerage display name (e.g. Sotheby's -> full name)
 */
export function tableBrokerageFilter(brokerage) {
  if (!brokerage) return brokerage;
  if (brokerage.includes("Sotheby's")) {
    return "Sotheby's International Realty";
  }
  if (brokerage && brokerage.includes("Berkshire")) {
    return "Berkshire Hathaway HomeServices California Properties";
  }
  return brokerage;
}

/**
 * Whether any property in agent list is agent-filtered.
 * Use when you have the full agent list (e.g. quarterlyAreaTable?.agentFilteredPhotoDetailsList).
 */
export function isAgentProperty(agentFilteredPhotoDetailsList) {
  if (!agentFilteredPhotoDetailsList || !Array.isArray(agentFilteredPhotoDetailsList)) {
    return false;
  }
  return agentFilteredPhotoDetailsList.some((property) => property.isAgentFiltered === true);
}

/**
 * Whether a single property is the agent's listing.
 * isAgentFiltered === true means agent listing ("your" listing); false means co-listing.
 * @param {Object} property - The property to check
 * @returns {boolean} true if agent property (your listing), false if not (e.g. co-listing — show warning)
 */
export function isPropertyAgentListing(property) {
  return property != null && property.isAgentFiltered === true;
}

/**
 * Split area string by & , / into array (mirrors PropertyUtil.splitAreas)
 */
export function splitAreas(area) {
  if (!area || typeof area !== "string") return [];
  let areaSplit = area
    .split(" & ")
    .join("/")
    .split(", ")
    .join("/")
    .split(",")
    .join("/")
    .split(" / ")
    .join("/")
    .split("/");
  if (areaSplit[areaSplit.length - 1] === "") {
    areaSplit = areaSplit.slice(0, -1);
  }
  return areaSplit;
}

/**
 * Returns true when HTML contains any meaningful visible text.
 */
export function hasMeaningfulHtmlText(htmlString = "") {
  const plainText = String(htmlString)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plainText.length > 0;
}

/**
 * Build display address from street parts. address key is always "-"; display is always derived from these 4 keys.
 */
export function deriveFullAddressFromPropertyKeys(property) {
  if (!property) return "";
  const isValidPart = (val) => {
    if (val === undefined || val === null) return false;
    const str = String(val).trim();
    return str !== "" && str !== "-" && str !== "0";
  };
  const parts = [
    property.streetNumber,
    property.streetDirPrefix,
    property.streetName,
    property.streetSuffix,
  ]
    .filter(isValidPart)
    .map((val) => String(val).trim());
  return parts.join(" ");
}

export function getPropertyTypeOptions() {
  return Object.entries(PROPERTY_TYPE_OPTIONS).map(([key, label]) => ({
    key,
    label,
  }));
}

/**
 * Street-number MLS override statuses shared by add/edit + table rendering logic.
 */
export function requiresStreetNumberOverrideStatus(status) {
  return (
    status === "Active" ||
    status === "Pending" ||
    status === "Contingent" ||
    status === "ActiveContingent"
  );
}

/**
 * Property-level override rule:
 * - Agent listings (isAgentFiltered=true) bypass MLS street-number override checks.
 * - Non-agent listings use status-based override rule.
 */
export function requiresStreetNumberOverrideForProperty(property) {
  if (property?.isAgentFiltered === true) return false;
  return requiresStreetNumberOverrideStatus(property?.status);
}

/**
 * Sort Sold rows by sale date DESC while preserving original order for non-Sold rows.
 */
export function sortSoldRowsBySaleDateDesc(list = []) {
  const getSaleDateTs = (p) => {
    const raw = p?.saleDate;
    if (!raw || raw === "-") return -Infinity;
    const ts = new Date(raw).getTime();
    return Number.isNaN(ts) ? -Infinity : ts;
  };
  const isSold = (p) => String(p?.status || "").toLowerCase() === "sold";
  return (list || [])
    .map((property, index) => ({ property, index }))
    .sort((a, b) => {
      const aSold = isSold(a.property);
      const bSold = isSold(b.property);
      if (aSold && bSold) {
        return getSaleDateTs(b.property) - getSaleDateTs(a.property) || a.index - b.index;
      }
      return a.index - b.index;
    })
    .map((x) => x.property);
}

/**
 * Title font size class from area character count
 */
export function getTitleFontSizeClass(areaSplit) {
  const characterCount = areaSplit.join("").length + (areaSplit?.length || 0);
  if (characterCount > 40) return "xs-market-sales-title";
  if (characterCount > 33) return "small-market-sales-title";
  if (characterCount > 27) return "medium-market-sales-title";
  return "regular-market-sales-title";
}